import { useEffect, useState, useCallback } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { Search, Download, SlidersHorizontal, ShieldCheck } from "lucide-react";
import { api, API } from "@/lib/api";
import { StatusStamp } from "@/components/StatusStamp";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { DocumentDetailModal } from "@/components/DocumentDetailModal";
import { CheckCircle2, Trash2, RotateCcw, AlertTriangle } from "lucide-react";
import { TelegramIcon } from "@/components/icons/TelegramIcon";
import { DOCS } from "@/constants/testIds";

const TITLES = {
  incoming: "Входящие документы",
  outgoing: "Исходящие документы",
  internal: "Внутренние документы",
  drafts: "Черновики",
  deleted: "Удалённые документы",
};

const DOC_TYPES = ["УПД", "Акт", "Договор", "Счет-фактура", "Накладная ТОРГ-12"];

const EMPTY_ADV = { doc_type: "", amount_min: "", amount_max: "", date_from: "", date_to: "", tags: "", project_id: "" };

function isOverdue(doc) {
  if (doc.status !== "in_progress") return false;
  const sentEntry = doc.history?.find((h) => h.action === "sent");
  const ts = sentEntry?.timestamp || doc.created_at;
  if (!ts) return false;
  return Date.now() - new Date(ts).getTime() > 3 * 24 * 60 * 60 * 1000;
}

export default function DocumentsList() {
  const { direction } = useParams();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [adv, setAdv] = useState(EMPTY_ADV);
  const context = useOutletContext();

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const params = {};
    if (direction === "drafts") {
      params.status = "draft";
    } else if (direction === "deleted") {
      params.deleted = true;
    } else {
      params.direction = direction;
      if (statusFilter !== "all") params.status = statusFilter;
    }
    if (search) params.search = search;
    if (adv.doc_type) params.doc_type = adv.doc_type;
    if (adv.amount_min) params.amount_min = adv.amount_min;
    if (adv.amount_max) params.amount_max = adv.amount_max;
    if (adv.date_from) params.date_from = adv.date_from;
    if (adv.date_to) params.date_to = adv.date_to;
    if (adv.tags) params.tags = adv.tags;
    if (adv.project_id) params.project_id = adv.project_id;
    return params;
  }, [direction, search, statusFilter, adv]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/documents", { params: buildParams() });
      setDocs(data);
      setSelectedIds([]);
    } catch {
      toast.error("Не удалось загрузить документы");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    load();
  }, [load]);

  const projectById = Object.fromEntries(projects.map((p) => [p.id, p]));

  const handleSign = async (doc, e) => {
    e.stopPropagation();
    try {
      await api.post(`/documents/${doc.id}/sign`);
      toast.success("Документ успешно подписан ЭЦП");
      load();
      context?.bumpRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка подписания");
    }
  };

  const handleDelete = async (doc, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Документ перемещён в удалённые");
      load();
      context?.bumpRefresh?.();
    } catch {
      toast.error("Ошибка удаления");
    }
  };

  const handleRestore = async (doc, e) => {
    e.stopPropagation();
    try {
      await api.post(`/documents/${doc.id}/restore`);
      toast.success("Документ восстановлен");
      load();
      context?.bumpRefresh?.();
    } catch {
      toast.error("Ошибка восстановления");
    }
  };

  const handleShare = async (doc, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/documents/${doc.id}/share`);
      const shareUrl = `${window.location.origin}/view/${data.share_token}`;
      window.open(shareUrl, "_blank", "noopener,noreferrer");
      toast.success("Ссылка для просмотра документа создана");
    } catch {
      toast.error("Не удалось создать ссылку для доступа");
    }
  };

  const openVerify = (doc, e) => {
    e.stopPropagation();
    window.open(`${window.location.origin}/verify/${doc.verification_code}`, "_blank");
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.length === docs.length ? [] : docs.map((d) => d.id)));
  };

  const bulkSign = async () => {
    try {
      const { data } = await api.post("/documents/bulk-sign", { ids: selectedIds });
      toast.success(data.message);
      load();
      context?.bumpRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось подписать документы");
    }
  };

  const bulkDelete = async () => {
    try {
      const { data } = await api.post("/documents/bulk-delete", { ids: selectedIds });
      toast.success(data.message);
      load();
      context?.bumpRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось удалить документы");
    }
  };

  const exportUrl = (path) => {
    const params = new URLSearchParams();
    Object.entries(buildParams()).forEach(([k, v]) => params.set(k, v));
    return `${API}${path}?${params.toString()}`;
  };

  return (
    <div data-testid="documents-list-page" data-direction={direction}>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{TITLES[direction] || "Документы"}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{docs.length} документ(ов)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.open(exportUrl("/documents/export"), "_blank")} data-testid="export-documents-button">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.open(exportUrl("/documents/export-pdf"), "_blank")} data-testid="export-documents-pdf-button">
            <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.open(exportUrl("/documents/export-1c"), "_blank")} data-testid="export-documents-1c-button">
            <Download className="w-3.5 h-3.5 mr-1.5" /> 1С (XML)
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3 bg-white p-3 rounded-xl border border-slate-200 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid={DOCS.searchInput} placeholder="Поиск по номеру или контрагенту" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {direction !== "drafts" && direction !== "deleted" && (
          <select data-testid={DOCS.statusFilter} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700">
            <option value="all">Все статусы</option>
            <option value="signed">Подписан</option>
            <option value="in_progress">Отправлено на подпись</option>
            <option value="rejected">Отказано</option>
          </select>
        )}
        <Button variant="ghost" size="sm" onClick={() => setShowAdvanced((v) => !v)} data-testid="toggle-advanced-search-button">
          <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" /> Расширенный поиск
        </Button>
      </div>

      {showAdvanced && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 bg-slate-50 p-3 rounded-xl border border-slate-200" data-testid="advanced-search-panel">
          <select value={adv.doc_type} onChange={(e) => setAdv({ ...adv, doc_type: e.target.value })} data-testid="adv-doc-type-select" className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white">
            <option value="">Любой тип</option>
            {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={adv.project_id} onChange={(e) => setAdv({ ...adv, project_id: e.target.value })} data-testid="adv-project-select" className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white">
            <option value="">Любой проект</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Input placeholder="Теги через запятую" value={adv.tags} onChange={(e) => setAdv({ ...adv, tags: e.target.value })} data-testid="adv-tags-input" />
          <div className="flex gap-1.5">
            <Input type="number" placeholder="Сумма от" value={adv.amount_min} onChange={(e) => setAdv({ ...adv, amount_min: e.target.value })} data-testid="adv-amount-min-input" />
            <Input type="number" placeholder="до" value={adv.amount_max} onChange={(e) => setAdv({ ...adv, amount_max: e.target.value })} data-testid="adv-amount-max-input" />
          </div>
          <Input type="date" value={adv.date_from} onChange={(e) => setAdv({ ...adv, date_from: e.target.value })} data-testid="adv-date-from-input" />
          <Input type="date" value={adv.date_to} onChange={(e) => setAdv({ ...adv, date_to: e.target.value })} data-testid="adv-date-to-input" />
          <Button size="sm" onClick={load} data-testid="apply-advanced-search-button" className="bg-slate-900 hover:bg-slate-800">Применить</Button>
          <Button size="sm" variant="outline" onClick={() => { setAdv(EMPTY_ADV); load(); }} data-testid="reset-advanced-search-button">Сбросить</Button>
        </div>
      )}

      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 mb-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5" data-testid="bulk-actions-bar">
          <span className="text-sm text-blue-800 font-medium">Выбрано: {selectedIds.length}</span>
          {direction !== "deleted" && (
            <>
              <Button size="sm" onClick={bulkSign} data-testid="bulk-sign-button" className="bg-blue-600 hover:bg-blue-700">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Подписать выбранные
              </Button>
              <Button size="sm" variant="outline" onClick={bulkDelete} data-testid="bulk-delete-button">
                <Trash2 className="w-3.5 h-3.5 mr-1" />Удалить выбранные
              </Button>
            </>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="px-4 py-3 w-10">
                <Checkbox checked={docs.length > 0 && selectedIds.length === docs.length} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
              </th>
              <th className="px-4 py-3 w-12">№</th>
              <th className="px-4 py-3">Документ</th>
              <th className="px-4 py-3">Контрагент</th>
              <th className="px-4 py-3">Сумма</th>
              <th className="px-4 py-3">Статус</th>
              <th className="px-4 py-3 text-right">Действия</th>
            </tr>
          </thead>
          <tbody>
            {!loading &&
              docs.map((doc, index) => (
                <tr key={doc.id} data-testid={`doc-row-${doc.id}`} onClick={() => setSelectedDoc(doc)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selectedIds.includes(doc.id)} onCheckedChange={() => {}} onClick={(e) => toggleSelect(doc.id, e)} data-testid={`select-doc-checkbox-${doc.id}`} />
                  </td>
                  <td className="px-4 py-3 text-slate-400 font-mono text-xs" data-testid={`doc-row-index-${doc.id}`}>{index + 1}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 flex items-center gap-1.5">
                      {doc.type} №{doc.number}
                      {doc.project_id && projectById[doc.project_id] && (
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: projectById[doc.project_id].color }} title={projectById[doc.project_id].name} />
                      )}
                    </div>
                    <div className="text-xs text-slate-500">от {doc.doc_date}</div>
                    {doc.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1" data-testid={`doc-row-tags-${doc.id}`}>
                        {doc.tags.map((t) => (
                          <span key={t} className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5">{t}</span>
                        ))}
                      </div>
                    )}
                    {doc.assigned_to_name && (
                      <div className="text-[11px] text-emerald-700 mt-0.5" data-testid={`doc-assigned-${doc.id}`}>→ {doc.assigned_to_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{doc.direction === "internal" ? "Внутри компании" : doc.counterparty_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-700 font-mono text-xs">{doc.amount?.toLocaleString("ru-RU")} ₽</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <StatusStamp status={doc.status} />
                      {isOverdue(doc) && (
                        <span title="Ожидает подписи более 3 дней" data-testid={`overdue-badge-${doc.id}`} className="text-amber-500">
                          <AlertTriangle className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {direction === "deleted" ? (
                      <Button size="sm" variant="outline" onClick={(e) => handleRestore(doc, e)} data-testid={`restore-doc-button-${doc.id}`}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />Восстановить
                      </Button>
                    ) : (
                      <>
                        {doc.status === "in_progress" && doc.direction === "incoming" && !doc.approval_route && (
                          <Button size="sm" onClick={(e) => handleSign(doc, e)} data-testid={`sign-doc-button-${doc.id}`} className="bg-blue-600 hover:bg-blue-700">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Подписать
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Публичная проверка подлинности"
                          onClick={(e) => openVerify(doc, e)}
                          data-testid={`verify-doc-button-${doc.id}`}
                          className="text-slate-400 hover:bg-slate-100"
                        >
                          <ShieldCheck className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Поделиться для изучения перед подписанием"
                          onClick={(e) => handleShare(doc, e)}
                          data-testid={`share-doc-button-${doc.id}`}
                          className="text-[#229ED9] hover:bg-sky-50 hover:text-[#229ED9]"
                        >
                          <TelegramIcon className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={(e) => handleDelete(doc, e)} data-testid={`delete-doc-button-${doc.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {!loading && docs.length === 0 && <div className="py-16 text-center text-slate-400 text-sm" data-testid="documents-empty-state">Документов не найдено</div>}
        {loading && <div className="py-16 text-center text-slate-400 text-sm">Загрузка…</div>}
      </div>

      {selectedDoc && (
        <DocumentDetailModal
          docId={selectedDoc.id}
          open={!!selectedDoc}
          onOpenChange={(v) => !v && setSelectedDoc(null)}
          onChanged={() => {
            load();
            context?.bumpRefresh?.();
          }}
        />
      )}
    </div>
  );
}
